/**
 * A small, safe Markdown reader for what coding agents write in a chat: headings, **bold**, *italic*, ~~strike~~, `code`, fenced
 * code, links, lists (nested, numbered, task), quotes, rules and GitHub-style tables. It builds a tree (never HTML), so nothing an
 * agent writes can inject markup; links keep only http(s) and mailto. Half-written text (a streamed reply) is fine: an unclosed
 * `**` stays literal until its closing mark arrives, an unclosed code fence runs to the end.
 */

export type Inline =
  | { t: "text"; v: string }
  | { t: "code"; v: string }
  | { t: "strong"; c: Inline[] }
  | { t: "em"; c: Inline[] }
  | { t: "del"; c: Inline[] }
  | { t: "link"; href: string; c: Inline[] }
  | { t: "br" };

export type Align = "left" | "center" | "right" | null;
export interface ListItem {
  /** A task-list item ("- [x] done"): `checked` says which. */
  task?: boolean;
  checked?: boolean;
  c: Block[];
}
export type Block =
  | { t: "p"; c: Inline[] }
  | { t: "h"; level: number; c: Inline[] }
  | { t: "code"; lang: string; v: string }
  | { t: "quote"; c: Block[] }
  | { t: "list"; ordered: boolean; start: number; items: ListItem[] }
  | { t: "hr" }
  | { t: "table"; align: Align[]; head: Inline[][]; rows: Inline[][][] };

const MAX_DEPTH = 8;
const PUNCT = /[!-/:-@[-`{-~]/;
const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c);
const isWord = (c: string | undefined) => c !== undefined && /[\p{L}\p{N}]/u.test(c);

// ---- inline -----------------------------------------------------------------------------------------------------

/** Index of a run of exactly `n` of `ch` at or after `from` (so ``` doesn't close a single `), or -1. */
function findRun(s: string, ch: string, n: number, from: number): number {
  let i = from;
  while (i < s.length) {
    if (s[i] !== ch) {
      i++;
      continue;
    }
    let r = 0;
    while (s[i + r] === ch) r++;
    if (r === n) return i;
    i += r;
  }
  return -1;
}

/** The `]` matching the `[` at `open` (nesting and code spans respected), or -1. */
function matchBracket(s: string, open: number): number {
  let depth = 0;
  for (let i = open; i < s.length; i++) {
    const c = s[i];
    if (c === "\\") i++;
    else if (c === "`") {
      let n = 1;
      while (s[i + n] === "`") n++;
      const close = findRun(s, "`", n, i + n);
      if (close >= 0) i = close + n - 1;
      else i += n - 1;
    } else if (c === "[") depth++;
    else if (c === "]" && --depth === 0) return i;
  }
  return -1;
}

const safeHref = (raw: string): string | null => {
  const url = raw.trim().replace(/^<|>$/g, "");
  if (/^https?:\/\/[^\s]+$/i.test(url) || /^mailto:[^\s]+$/i.test(url)) return url;
  return null;
};

/** Parses `(url "title")` starting at the "(" at `i`; returns the url and the index after ")". */
function linkTarget(s: string, i: number): { href: string; end: number } | null {
  if (s[i] !== "(") return null;
  let depth = 0;
  for (let j = i; j < s.length; j++) {
    const c = s[j];
    if (c === "\\") j++;
    else if (c === "(") depth++;
    else if (c === ")" && --depth === 0) {
      const inner = s.slice(i + 1, j).trim();
      const href = safeHref(/^<[^>]*>/.exec(inner)?.[0] ?? inner.split(/\s+/)[0] ?? "");
      return href ? { href, end: j + 1 } : null;
    }
  }
  return null;
}

/** Closing mark of an emphasis run opened at `from` (after the opener), skipping code spans and escapes. */
function findClose(s: string, ch: string, n: number, from: number): number {
  for (let i = from; i < s.length; i++) {
    const c = s[i];
    if (c === "\\") {
      i++;
      continue;
    }
    if (c === "`") {
      let r = 1;
      while (s[i + r] === "`") r++;
      const close = findRun(s, "`", r, i + r);
      if (close >= 0) i = close + r - 1;
      else i += r - 1;
      continue;
    }
    if (c !== ch) continue;
    let r = 0;
    while (s[i + r] === ch) r++;
    // a run of exactly the opener's length, closing right after text (not after a space)
    if (r === n && i > from && !isSpace(s[i - 1]) && (ch !== "_" || !isWord(s[i + r]))) return i;
    if (n === 2 && r === 3 && ch !== "~" && i > from && !isSpace(s[i - 1]) && (s[i + r] === undefined || !isWord(s[i + r]))) return i + 1; // **bold *em***: the last two marks close
    i += r - 1;
  }
  return -1;
}

export function parseInline(s: string, depth = 0): Inline[] {
  const out: Inline[] = [];
  let buf = "";
  const flush = () => {
    if (buf) out.push({ t: "text", v: buf });
    buf = "";
  };
  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;
    if (ch === "\\" && i + 1 < s.length) {
      if (s[i + 1] === "\n") {
        flush();
        out.push({ t: "br" });
      } else buf += PUNCT.test(s[i + 1]!) ? s[i + 1] : ch + s[i + 1];
      i += 2;
      continue;
    }
    if (ch === "\n") {
      buf = buf.replace(/ +$/, "");
      flush();
      out.push({ t: "br" });
      i++;
      continue;
    }
    if (ch === "`") {
      let n = 1;
      while (s[i + n] === "`") n++;
      const close = findRun(s, "`", n, i + n);
      if (close >= 0) {
        let v = s.slice(i + n, close).replace(/\n/g, " ");
        if (v.length > 2 && v.startsWith(" ") && v.endsWith(" ") && v.trim()) v = v.slice(1, -1);
        flush();
        out.push({ t: "code", v });
        i = close + n;
      } else {
        buf += "`".repeat(n);
        i += n;
      }
      continue;
    }
    if ((ch === "[" || (ch === "!" && s[i + 1] === "[")) && depth < MAX_DEPTH) {
      const open = ch === "!" ? i + 1 : i;
      const close = matchBracket(s, open);
      const target = close > 0 ? linkTarget(s, close + 1) : null;
      if (target) {
        const label = s.slice(open + 1, close);
        flush();
        // an image is shown as a link: nothing is fetched on the person's behalf
        out.push({ t: "link", href: target.href, c: parseInline(label || (ch === "!" ? "image" : target.href), depth + 1) });
        i = target.end;
        continue;
      }
    }
    if (ch === "<") {
      const m = /^<((?:https?:\/\/|mailto:)[^\s<>]+)>/i.exec(s.slice(i));
      if (m) {
        flush();
        out.push({ t: "link", href: m[1]!, c: [{ t: "text", v: m[1]! }] });
        i += m[0].length;
        continue;
      }
    }
    if ((ch === "h" || ch === "H") && !isWord(s[i - 1])) {
      const m = /^https?:\/\/[^\s<>]+/i.exec(s.slice(i));
      if (m) {
        let url = m[0];
        // trailing punctuation belongs to the sentence; a ")" only when it has no "(" to close
        for (;;) {
          const last = url[url.length - 1]!;
          if (/[.,;:!?'"*_~\]]/.test(last) || (last === ")" && (url.match(/\(/g)?.length ?? 0) < (url.match(/\)/g)?.length ?? 0))) url = url.slice(0, -1);
          else break;
        }
        if (url.length > 8) {
          flush();
          out.push({ t: "link", href: url, c: [{ t: "text", v: url }] });
          i += url.length;
          continue;
        }
      }
    }
    if ((ch === "*" || ch === "_" || ch === "~") && depth < MAX_DEPTH) {
      let r = 1;
      while (s[i + r] === ch) r++;
      const n = ch === "~" ? (r === 2 ? 2 : 0) : r <= 3 ? r : 0;
      const opens = n > 0 && !isSpace(s[i + r]) && (ch !== "_" || !isWord(s[i - 1]));
      if (opens) {
        const close = findClose(s, ch, n, i + n);
        if (close > 0) {
          const inner = parseInline(s.slice(i + n, close), depth + 1);
          flush();
          if (ch === "~") out.push({ t: "del", c: inner });
          else if (n === 1) out.push({ t: "em", c: inner });
          else if (n === 2) out.push({ t: "strong", c: inner });
          else out.push({ t: "strong", c: [{ t: "em", c: inner }] });
          i = close + n;
          continue;
        }
      }
      buf += ch.repeat(r);
      i += r;
      continue;
    }
    buf += ch;
    i++;
  }
  flush();
  return out;
}

// ---- blocks -----------------------------------------------------------------------------------------------------

const FENCE = /^( {0,3})(`{3,}|~{3,})[ \t]*([^\s`]*)[^`]*$/;
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/;
const HR = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const MARKER = /^( *)([-*+]|\d{1,9}[.)])(?: +(.*)|)$/;
const QUOTE = /^ {0,3}>/;
const TABLE_DELIM = /^\s*\|?\s*:?-{1,}:?\s*(?:\|\s*:?-{1,}:?\s*)*\|?\s*$/;

const indentOf = (l: string) => /^ */.exec(l)![0].length;

/** Split a table row on its unescaped pipes (one inside `code` stays in the cell). */
function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i]!;
    if (c === "\\" && s[i + 1] === "|") {
      cur += "|";
      i++;
    } else if (c === "`") {
      let n = 1;
      while (s[i + n] === "`") n++;
      const close = findRun(s, "`", n, i + n);
      const end = close >= 0 ? close + n : i + n;
      cur += s.slice(i, end).replace(/\\\|/g, "|");
      i = end - 1;
    } else if (c === "|") {
      cells.push(cur.trim());
      cur = "";
    } else cur += c;
  }
  cells.push(cur.trim());
  return cells;
}

const delimAlign = (cell: string): Align => {
  const c = cell.trim();
  const l = c.startsWith(":");
  const r = c.endsWith(":");
  return l && r ? "center" : r ? "right" : l ? "left" : null;
};

const tableAt = (lines: string[], i: number): boolean => {
  const head = lines[i];
  const delim = lines[i + 1];
  if (head === undefined || delim === undefined || !head.includes("|") || !delim.includes("-") || !TABLE_DELIM.test(delim)) return false;
  if (!delim.includes("|") && !head.trim().startsWith("|")) return false; // "a" over "---" is a heading underline, not a table
  return splitRow(head).length === splitRow(delim).length;
};

const startsBlock = (lines: string[], i: number): boolean => {
  const l = lines[i]!;
  if (FENCE.test(l) || HEADING.test(l) || HR.test(l) || QUOTE.test(l) || tableAt(lines, i)) return true;
  const m = MARKER.exec(l);
  return !!m && (/^[-*+]$/.test(m[2]!) || m[2]!.startsWith("1"));
};

export function parseBlocks(src: string, depth = 0): Block[] {
  const lines = src.replace(/\r\n?/g, "\n").replace(/\t/g, "    ").split("\n");
  const blocks: Block[] = [];
  const n = lines.length;
  let i = 0;
  while (i < n) {
    const line = lines[i]!;
    if (line.trim() === "") {
      i++;
      continue;
    }
    const fence = FENCE.exec(line);
    if (fence) {
      const [, ind, marks, lang] = fence;
      const body: string[] = [];
      i++;
      while (i < n) {
        const close = /^ {0,3}(`{3,}|~{3,})[ \t]*$/.exec(lines[i]!);
        if (close && close[1]![0] === marks![0] && close[1]!.length >= marks!.length) {
          i++;
          break;
        }
        body.push(lines[i]!.replace(new RegExp(`^ {0,${ind!.length}}`), ""));
        i++;
      }
      blocks.push({ t: "code", lang: lang ?? "", v: body.join("\n") });
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      blocks.push({ t: "h", level: h[1]!.length, c: parseInline((h[2] ?? "").trim()) });
      i++;
      continue;
    }
    if (HR.test(line)) {
      blocks.push({ t: "hr" });
      i++;
      continue;
    }
    if (QUOTE.test(line) && depth < MAX_DEPTH) {
      const inner: string[] = [];
      while (i < n && QUOTE.test(lines[i]!)) inner.push(lines[i++]!.replace(/^ {0,3}> ?/, ""));
      blocks.push({ t: "quote", c: parseBlocks(inner.join("\n"), depth + 1) });
      continue;
    }
    if (tableAt(lines, i)) {
      const head = splitRow(lines[i]!);
      const align = splitRow(lines[i + 1]!).map(delimAlign);
      i += 2;
      const rows: Inline[][][] = [];
      while (i < n && lines[i]!.trim() !== "" && lines[i]!.includes("|") && !FENCE.test(lines[i]!)) {
        const cells = splitRow(lines[i++]!);
        while (cells.length < head.length) cells.push("");
        rows.push(cells.slice(0, head.length).map((c) => parseInline(c)));
      }
      blocks.push({ t: "table", align, head: head.map((c) => parseInline(c)), rows });
      continue;
    }
    if (MARKER.test(line) && !HR.test(line) && depth < MAX_DEPTH) {
      const r = parseList(lines, i, depth);
      blocks.push(r.block);
      i = r.next;
      continue;
    }
    // a paragraph: runs until a blank line or the start of another block; the line breaks inside it are kept
    const para = [line.trim()];
    i++;
    while (i < n && lines[i]!.trim() !== "" && !startsBlock(lines, i)) para.push(lines[i++]!.trim());
    blocks.push({ t: "p", c: parseInline(para.join("\n")) });
  }
  return blocks;
}

function parseList(lines: string[], start: number, depth: number): { block: Block; next: number } {
  const n = lines.length;
  const first = MARKER.exec(lines[start]!)!;
  const base = first[1]!.length;
  const ordered = /\d/.test(first[2]!);
  const items: ListItem[] = [];
  let i = start;
  while (i < n) {
    // blank lines between items are fine when another item of this list follows
    let j = i;
    while (j < n && lines[j]!.trim() === "") j++;
    const m = j < n ? MARKER.exec(lines[j]!) : null;
    if (!m || HR.test(lines[j]!) || m[1]!.length >= base + 2 || m[1]!.length + 1 < base || /\d/.test(m[2]!) !== ordered) break;
    i = j;
    const markerIndent = m[1]!.length;
    let cut = markerIndent + m[2]!.length + 1;
    const content = [m[3] ?? ""];
    i++;
    while (i < n) {
      const l = lines[i]!;
      if (l.trim() === "") {
        let k = i;
        while (k < n && lines[k]!.trim() === "") k++;
        if (k < n && indentOf(lines[k]!) >= cut) {
          content.push("");
          i++;
          continue;
        }
        break;
      }
      const ind = indentOf(l);
      if (ind < cut && ind > markerIndent && MARKER.test(l)) cut = ind; // a nested list indented less than the text above it
      if (ind >= cut) {
        content.push(l.slice(cut));
        i++;
        continue;
      }
      if (ind <= markerIndent + 1 && (MARKER.test(l) || startsBlock(lines, i))) break;
      content.push(l.trim()); // a wrapped line of the item's text
      i++;
    }
    const task = /^\[([ xX])\][ \t]+/.exec(content[0]!);
    if (task) content[0] = content[0]!.slice(task[0].length);
    items.push({ ...(task ? { task: true, checked: task[1] !== " " } : {}), c: parseBlocks(content.join("\n"), depth + 1) });
  }
  return { block: { t: "list", ordered, start: ordered ? parseInt(first[2]!, 10) : 1, items }, next: i };
}

/** Plain text of some inline nodes (for labels and tests). */
export const inlineText = (c: Inline[]): string => c.map((n) => (n.t === "text" || n.t === "code" ? n.v : n.t === "br" ? "\n" : inlineText(n.c))).join("");
